/**
 * @module config/page
 * @description Page copy and settings AFJ would edit from the CMS (brief §6):
 * hero slides, headline, impact strip, gift presets, trust statements.
 *
 * Impact-strip figures are AFJ's OWN published numbers (Samantha's approved
 * card copy, afj-pledge-addon seed-cards.json), not sample data.
 */

/** One hero slide. */
export interface HeroSlide {
    src: string;
    alt: string;
}

/** 5 rotating hero images (brief §4.1: four to five). */
export const HERO_SLIDES: readonly HeroSlide[] = [
    { src: "causes/40-years-of-giving.jpg", alt: "A waterfall meets the sea on Jamaica's north coast" },
    { src: "causes/cove-primary-school.jpg", alt: "A Cove Primary School student smiles and makes a peace sign" },
    { src: "causes/breds-treasure-beach.jpg", alt: "Fishing boats flying Jamaican flags off Treasure Beach" },
    { src: "causes/kingston-creative.jpg", alt: "A Kingston Creative street mural downtown" },
    { src: "causes/a-year-of-giving-2025.jpg", alt: "AFJ grant recipients gathered at the 2025 grant ceremony" },
];

/** Milliseconds each hero slide stays before advancing. */
export const HERO_INTERVAL_MS: number = 6000;

/** Hero copy (brief §4.1, mockup wording). */
export const HERO_COPY = {
    kicker: "The American Friends of Jamaica",
    headline: "Choose a Cause. Change a Community.",
    subline: "Give to vetted grassroots partners across Jamaica, several causes at once, in one secure checkout.",
    browseCta: "Browse all causes",
    featuredCta: "See featured causes",
    searchPlaceholder: "Search causes, partners or places",
} as const;

/** One impact-strip figure. `source` says where it comes from (truthful-copy rule). */
export interface ImpactFigure {
    value: string;
    label: string;
    source: "afj" | "sample";
}

/** Impact strip: AFJ published figures + the live count of causes on this page. */
export function impactFigures(activeCauseCount: number): ImpactFigure[] {
    return [
        { value: "US$32M+", label: "mobilized for Jamaica since 1982", source: "afj" },
        { value: "63", label: "grant recipients, Apr–Sep 2025", source: "afj" },
        { value: String(activeCauseCount), label: "sample causes on this page", source: "sample" },
    ];
}

/** Preset gift amounts in whole USD (brief §4.5). */
export const GIFT_PRESETS: readonly number[] = [25, 50, 100, 250];

/** Amount used by a card's one-tap Add to Cart (editable in the cart). */
export const DEFAULT_GIFT_USD: number = 50;

/** Causes shown per "Load more" step (brief §4.3). */
export const PAGE_SIZE: number = 6;

/** Trust statements (brief §4.7), each with an icon id from components/Icons. */
export const TRUST_POINTS: readonly { icon: "lock" | "receipt" | "shield" | "report"; title: string; body: string }[] = [
    { icon: "lock", title: "Secure payment", body: "Processed by Neon's secure payment system and SSL protected. Card details never touch this page." },
    { icon: "receipt", title: "Tax-deductible, receipted", body: "Every gift is a tax-deductible gift to AFJ, with an emailed receipt." },
    { icon: "shield", title: "Vetted partners", body: "Gifts are designated to verified, vetted Jamaican nonprofit partners." },
    { icon: "report", title: "Registered 501(c)(3)", body: "AFJ is a registered 501(c)(3) with transparent annual impact reporting." },
];

/** The one disclosure line shown on every demo screen (truthful-copy rule). */
export const DEMO_NOTICE: string =
    "Demo · Partner photos and impact facts are AFJ's. Goals, totals, donor counts and cause titles are sample content. No payment is taken.";
